
import { v4 as uuidv4 } from "uuid";
import { 
  FloatingFeature,
  PlayerObject,
} from "@models";
import { 
  Creature,
  AbilityScores,
  CharacterFeature,
  UpgradableNumber,
  CreatureGift,
  DamageTypeOverridesTable,
  CharacterSubscriptionHolder,
  SKILLS,
  CharacterCondition,
  CreatureAbility,
  CharacterFeatureBase,
  CharacterFeatureSource,
  Ability,
  MinionAbility,
  CharacterMind,
  CharacterBody,
  Spirit,
  HitDiceUpgradable,
  SpellcastingEntry,
  TableEntry,
  Spell,
  Attack,
  CharacterSpell,
  CharacterCustomizations,
} from "@dndModels";
import {  
  SIZE_MAP, 
} from "@dndModels/Constants";
import { AbilityScoreType, CreatureType, DamageType, DataType, SizeType, SpeedType, isDamageType } from "@dndModels/Types";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

/**
 * This is going to replace all the other creature instances. 
 * It will work more like a character, but is simpler than an actual character. 
 * It will have Creature and Summon and Transform as possible feature sources.
 * It will be processed like a Character, but due to its simpler nature, it will be faster and easier to process.
 * 
 * It uses the spirit, mind, and body model like I recently did for Character, 
 * which will make it easier to make the processing work, as it will have a lot of overlap with how I do it for Character.
 * 
 * One big difference from how I was doing it before is that I'm not going to bother trying to connect up a 
 * summon stat block with all the relevant parts used when creating it.
 * Instead it will hold all the relevant things as part of the spirit. 
 * When it's summoned, or otherwise created it will get everything copied into spirit, 
 * which gets saved to the db and loaded out of it again, making it so we don't have to search for it.
 */
export class TrueCreatureInstance extends PlayerObject { 
  data_type: DataType = "complex_creature";

  spirit = new CreatureSpirit();
  mind = new CharacterMind();
  _body = new CharacterBody();
  inherited_body?: CharacterBody;
  get body() {
    return this.inherited_body ? this.inherited_body : this._body;
  }
  customizations = new CharacterCustomizations();

  owner_id = "";
  _num = 0;
  _true_id = "";
  get true_id() { return this._id === "" ? this._true_id : ""; }
  get creature_id() { return this.spirit.creature_id; }
  get creature() { return this.spirit.creature; }
  get alignment() { return this.spirit.alignment; }
  get small_name() { return this.spirit.small_name; }
  get title() { return this.spirit.title; }
  get use_name() {
    if (this.creature) {
      if (this.name === "") {
        return this.creature.name;
      }
    } 
    return this.name;
  }
  get_use_name() {
    return this.use_name;
  }
  get_speed(speed: string) {
    return (this.speed as any)[speed];
  }
  get spellcasting_ability() { return this.creature?.spellcasting_ability??"None"; };
  
  get creature_type() { return this.customizations.creature_type ? this.customizations.creature_type : this.mind.creature_type; }
  // get subtypes() { return this.mind.subtypes; }
  get tags() { return this.mind.tags; }
  get age_category() { return this.mind.age_category; }
  get subtypes() {
    if (this.creature) {
      return this.creature.subtypes;
    }
    return [];
  }
  get swarm() { // This should be changed to be a Spirit property
    return this.creature ? this.creature.swarm : false;
  }
  get creature_type_string() {
    if (this.swarm) {
      return `Swarm of ${this.size} ${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    } else {
      return `${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    } 
  }
  
  get size() { return SIZE_MAP[this.mind.use_size]; }
  get use_size() { return this.mind.use_size; }
  get size_num() { return this.mind.use_size; }
  
  get character_level() { return this.mind.character_level; }
  get challenge_rating() { return this.mind.character_level; }
  get use_challenge_rating() { return this.mind.character_level; }
  
  get current_ability_scores() { return this.mind.current_ability_scores; }

  get hp() { return this.body.hp; }
  get proficiency_modifier() { return this.mind.proficiency_modifier; }
  get hit_dice() { return this.body.hit_dice; }

  get hit_dice_string() {
    let response = "";
    for (let i = 0; i < this.hit_dice.length; i++) {
      const hd = this.hit_dice[i];
      if (response !== "") {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
    }
    if (this.mind.current_ability_scores.CON !== 0) {
      response += " " + common_util.addPlusMaybe(this.mind.current_ability_scores.CON * common_util.sum(this.hit_dice.map(o => o.count)));
    }
    return response;
  }

  get speed() { return this.mind.speed; }
  get swimming() { return this.mind.speed.swim > 0; }
  get flying() { return this.mind.speed.fly > 0; }
  get burrowing() { return this.mind.speed.burrow > 0; }
  get speed_string(): string {
    let response = "";
    const speeds = Object.keys(this.speed) as SpeedType[];
    for (const key of speeds) {
      if (this.speed[key]) {
        if (response.length > 0) {
          response += ", ";
        }
        response += `${key} ${this.speed[key]} ft.`;
        if (key === "fly") {
          if (this.spirit.hover) {
            response += " (hover)";
          }
        }
      }
    }
    return response;
  }
  
  get conditions() { return this.spirit.conditions; }
  set conditions(value: CharacterCondition[]) { this.spirit.conditions = value; }

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }
  // The damage types its traits and actions deal.
  get damage_types_dealt(): DamageType[] {
    const ffs = this.spirit.ffs;
    const from_ffs = [...ffs.traits, ...ffs.actions, ...ffs.legendary_actions, ...ffs.lair_actions].flatMap(o => o.damage_types);
    const from_creature = this.spirit.creature ? this.spirit.creature.damage_types_dealt : [];
    return common_util.unique_array([...from_ffs, ...from_creature]
      .filter((o): o is DamageType => isDamageType(o) && o !== "Typeless" && o !== "Same as Main"));
  }

  // The damage types it resists or is immune to, including anything that's changed them since it was made.
  get damage_types_resisted(): DamageType[] {
    return common_util.unique_array([
      ...this.mind.getDTsForMultiplier(0),
      ...this.mind.getDTsForMultiplier(0.5),
      ...this.spirit.damage_immunities,
      ...this.spirit.damage_resistances,
    ].filter((o): o is DamageType => isDamageType(o)));
  }

  get immunities_string() {
    let str = this.mind.damage_immunities.join(", ");
    if (this.mind.extra_damage_immunities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.mind.extra_damage_immunities;
    }
    return str;
  }
  get resistances_string() {
    let str = this.mind.damage_resistances.join(", ");
    if (this.mind.extra_damage_resistances.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.mind.extra_damage_resistances;
    }
    return str;
  }
  get vulnerabilities_string() {
    let str = this.mind.damage_vulnerabilities.join(", ");
    if (this.mind.extra_damage_vulnerabilities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.mind.extra_damage_vulnerabilities;
    }
    return str;
  }
  get condition_immunities() { return this.spirit.condition_immunities; }
  get condition_immunities_string() { return this.spirit.condition_immunities.join(", "); }

  get char_feature_sources() { return this.mind.char_feature_sources; }
  get cgift_ids() { return this.spirit.cgift_ids; }
  set cgift_ids(value: string[]) { this.spirit.cgift_ids = value; }
  get creature_gifts() { return this.spirit.creature_gifts; }

  get base_size() { return this.spirit.base_size; }
  get size_modifier() { return this.mind.size_modifier; }
  get senses() { return this.mind.senses; }
  get passive_bonuses() { return this.mind.passive_bonuses; }
  get advantage() { return this.body.advantage; }

  get skills_string(): string {
    return this.skill_proficiencies2.map(o => { return `${o.skill_name}: ${o.modifier < 0 ? o.modifier : "+" + o.modifier}`; }).join(", ");
  }
  get skill_proficiencies2(): { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] {
    const profs: { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] = [];
    // Skills with Customizations are shown even if the creature isn't proficient in them.
    const skill_ids = common_util.unique_array([
      ...this.mind.skill_proficiencies.map(o => o.proficiency_id),
      ...Object.keys(this.customizations.prof_overrides)
    ]);
    for (const skill_id of skill_ids) {
      const skill = SKILLS.find(o => o._id === skill_id || o.name === skill_id);
      if (skill && !profs.some(o => o.skill_id === skill._id)) {
        // skill_checks has everything processed, including Customizations.
        const skill_check = this.mind.skill_checks.find(o => o.skill_id === skill._id);
        if (skill_check) {
          profs.push({ skill_id: skill._id, prof_level: skill_check.skill_check.proficiency, skill_name: skill.name, modifier: skill_check.skill_check.modifier });
        } else {
          const prof_level = this.mind.skill_proficiencies.find(o => o.proficiency_id === skill._id || o.proficiency_id === skill.name)?.proficiency_level??0;
          const modifier = this.proficiency_modifier * prof_level + this.current_ability_scores.getModifier(skill.use_ability_score);
          profs.push({ skill_id: skill._id, prof_level, skill_name: skill.name, modifier });
        }
      }
    }
    return profs;
  }

  get tools_string() { return this.mind.tool_proficiencies.join(", "); }

  get senses_string() {
    const response = this.senses.map(o => `${o.name}: ${o.range} ft.`).join(", ");
    return response;
  }
  get passive_perception() {
    const perception_check = this.mind.skill_checks.find(o => o.skill_id === "5fef742e7494ba20cca8a9ec");
    if (perception_check) {
      return 10 + perception_check.skill_check.modifier;
    }
    let passive_perception = 10 + this.current_ability_scores.WIS;
    const perception = this.mind.skill_proficiencies.find(o => o.proficiency_id === "5fef742e7494ba20cca8a9ec" || o.proficiency_id === "Perception");
    if (perception) {
      passive_perception += this.proficiency_modifier * perception.proficiency_level;
    }
    return passive_perception;
  }

  get resource_holder() { return this.body.resource_holder; }
  get resources() { return this.body.resources; }
  get use_resources() {
    const use_resources: {
      resource_id: string, // Can be "Slot:Level" or "Pact:Level"
      used: number,
      total: number,
      size: number
    }[] = [];
    const resources = this.resource_holder;
    for (const slot of resources.slots) {
      const resource_id = `${slot.slot_name}:${slot.level}`;
      const used = slot.used;
      const total = slot.total;
      const size = 0;
      use_resources.push({ resource_id, used, total, size });
    }
    for (const resource of resources.resources) {
      const resource_id = resource.type_id;
      const used = resource.used;
      const total = resource.total;
      const size = resource.size;
      use_resources.push({ resource_id, used, total, size });
    }
    return use_resources;
  }
  get legendary_resistances() { return this.spirit.legendary_resistances; }
  get used_legendary_resistances() { return this.body.resource_holder.used_legendary_resistances; }
  get used_legendary_actions() { return this.body.resource_holder.used_legendary_actions; }
  get legendary_actions_per_round() { return this.spirit.legendary_actions_per_round; } // This should probably get it from mind instead of spirit.

  get use_spells() {
    // THIS IS HOW IT'S DONE FOR BASIC ONES. 
    // I NEED TO CHANGE IT TO WORK WITH CREATURE GIFTS AND OTHER FEATURE SOURCES AS WELL.
    if (this.spirit.creature) {
      const spellcasting_entry = this.spirit.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.spells;
      }
    }
    return [];
  }

  get spellcasting_description(): string {
    if (this.spirit.creature) {
      const spellcasting_entry = this.spirit.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.description;
      }
    }
    return "";
  }

  get use_slots2() { return this.use_resources.filter(o => o.resource_id.includes("Slot")); }
  
  get initiative_modifier() { return this.mind.initiative_modifier; }
  get passive_initiative() { return this.mind.passive_initiative; }
  get armor_class() { return this.mind.armor_class; }
  get use_ac() { return this.mind.armor_class; }
  
  get_saving_throw(ability: AbilityScoreType) {
    const roll = this.mind.saving_throws[ability];
    if (roll) {
      return roll.modifier;
    } else {
      const modifier = this.current_ability_scores.getModifier(ability);
      if (!modifier) {
        return 0
      } else {
        return modifier;
      }
    }
  }
  saving_throw(ability: AbilityScoreType) {
    return this.get_saving_throw(ability);
  }
  
  get extra_attacks() { return this.mind.extra_attacks; }
  
  get full_action_action_types() { return this.mind.full_actions; }
  get bonus_action_action_types() { return this.mind.bonus_actions; }
  get reaction_action_types() { return this.mind.reactions; }

  get traits() { return this.mind.traits; }
  get trait_cfs() { return this.mind.traits.filter(o => o.char_feature).map(o => o.char_feature) as CharacterFeature[]; }
  get actions() { return this.mind.actions; }
  get action_cfs() {
    return this.actions.filter(o => 
      o.char_feature &&
      (!(o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) || 
      (o.floating_feature.casting_time !== "BA" && o.floating_feature.casting_time !== "RA"))).map(o => o.char_feature) as CharacterFeature[];
  }
  get bonus_actions() {
    return this.actions.filter(o => 
      o.char_feature &&
      (o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) && 
      o.floating_feature.casting_time === "BA").map(o => o.char_feature) as CharacterFeature[];
  }
  get reactions() {
    return this.actions.filter(o => 
      o.char_feature &&
      (o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) && 
      o.floating_feature.casting_time === "RA").map(o => o.char_feature) as CharacterFeature[];
  }
  get legendary_actions() { return this.mind.legendary_actions; }
  get legendary_action_cfs() { return this.mind.legendary_actions.filter(o => o.char_feature).map(o => o.char_feature) as CharacterFeature[]; }
  get lair_actions() { return this.mind.lair_actions; }
  get lair_action_cfs() { return this.mind.lair_actions.filter(o => o.char_feature).map(o => o.char_feature) as CharacterFeature[]; }
  
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  connected = false;
  changed() {
    this.subscription_holder.characterChanged();
  }
  
  constructor(obj?: any) {
    super(obj);
    this._true_id = obj && obj._true_id ? obj._true_id : uuidv4().toString();
    if (obj) {
      this.spirit = obj.spirit ? new CreatureSpirit(obj.spirit) : new CreatureSpirit();
      this._body = obj.body ? new CharacterBody(obj.body) : new CharacterBody();
      this.owner_id = obj.owner_id ? obj.owner_id : "";
      this._num = obj._num ? obj._num : 0;
      this.customizations = obj.customizations ? new CharacterCustomizations(obj.customizations) : new CharacterCustomizations();
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      _true_id: this._true_id,
      spirit: this.spirit.toDBObj(),
      body: this.body.toDBObj(),
      owner_id: this.owner_id,
      _num: this._num,
      customizations: this.customizations.toDBObj()
    };
  }

  clone(drop_ids = false): TrueCreatureInstance {
    const clone = new TrueCreatureInstance();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: TrueCreatureInstance, drop_ids = false): void {
    super.copy(copyMe);
    if (!drop_ids) {
      this._true_id = copyMe._true_id;
    }
    this.spirit = copyMe.spirit.clone();
    // this.mind = copyMe.mind;
    this._body = copyMe._body.clone();
    this.customizations = copyMe.customizations.clone();
    this.owner_id = copyMe.owner_id;
    this._num = copyMe._num;
  }
}
export class CreatureSpirit implements Spirit {
  base_size: SizeType = 3;
  base_ability_scores = new AbilityScores();
  cr = 0;
  creature_type: CreatureType = "None";
  subtypes: string[] = [];
  small_name = "";
  title = "";

  alignment = "";
  ac_flavor = "";
  base_ac = new UpgradableNumber();
  age_category = "";
  swarm = false;
  tags: string[] = [];
  init = 0;
  use_init = false; // If true then init is used as the base Initiative Modifier instead of the DEX Modifier. Transforms use this.
  hit_dice: HitDiceUpgradable[] = [];
  hit_dice_bonus = new UpgradableNumber();
  max_hp = 0;
  calc_max_hp_from_source = false;
  get calc_max_hp() {
    if (this.calc_max_hp_from_source || this.creature_gifts.some(o => o.source && o.source.calc_max_hp)) {
      return true;
    }
    return false;
  }
  passive_perception = 0;
  spell_attack = new UpgradableNumber();
  spell_dc = new UpgradableNumber();
  spellcasting_ability: AbilityScoreType | "None" = "None";
  sc_entry?: SpellcastingEntry;

  // These are details about the summoner at the time of the summoning, if that applies.
  // Summon Stat Blocks frequently use these. 
  // For Attributes on the Creature itself they will just be calculated at the time of the summoning and then stored,
  // but these are often also used in the abilities, so we need to keep them here so they can be used when processing those.
  summoner_attrs?: SummonerAttributes;
  use_summoner_pb = false;

  creature_id = ""; // This is only used for getting description.
  creature?: Creature;

  // This holds the ids of the Floating Features from the Creature or Summon Stat Block used to create it.
  ff_ids: { traits: string[], actions: string[], legendary_actions: string[], lair_actions: string[] } = {
    traits: [], actions: [], legendary_actions: [], lair_actions: []
  };
  // This holds the actual Floating Features. 
  // They don't get saved as part of the Creature Instance, so they will need to be connected on load, using the ids above.
  ffs: { traits: FloatingFeature[], actions: FloatingFeature[], legendary_actions: FloatingFeature[], lair_actions: FloatingFeature[] } = {
    traits: [], actions: [], legendary_actions: [], lair_actions: []
  };
  inherited_spells: InheritedSpell[] = [];
  char_spells: CharacterSpell[] = [];
  legendary_actions_per_round = 0;
  legendary_resistances = 0;
  cgift_ids: string[] = [];
  creature_gifts: CreatureInstanceGift[] = [];

  conditions: CharacterCondition[] = [];
  condition_immunities: string[] = [];
  damage_immunities: DamageType[] = [];
  damage_resistances: DamageType[] = [];
  damage_vulnerabilities: DamageType[] = [];
  extra_damage_immunities = "";
  extra_damage_resistances = "";
  extra_damage_vulnerabilities = "";
  senses: { [sense_name: string]: number } = {};
  save_profs: string[] = [];
  skill_profs: { [skill_id: string]: number } = {};
  speeds: { [speed_type: string]: number } = {};
  hover = false;
  tool_profs: string[] = [];
  languages = "";

  // For now this is empty, but I'm going to make it possible to add in feature sources like Creature Gifts, etc.
  get char_feature_sources(): CharacterFeatureSource[] {
    let char_feature_sources: CharacterFeatureSource[] = [];
    char_feature_sources = [];
    let i = 0;
    while (i < char_feature_sources.length) {
      const cfs = char_feature_sources[i];
      char_feature_sources = [...char_feature_sources,...cfs.sub_char_feature_sources];
      ++i;
    }
    return char_feature_sources;
  }

  get character_level() {
    return this.cr;
  }

  constructor(obj?: any) {
    if (obj) {
      this.small_name = obj.small_name ? obj.small_name : "";
      this.title = obj.title ? obj.title : "";
      this.alignment = obj.alignment ? obj.alignment : "";
      this.ac_flavor = obj.ac_flavor ? obj.ac_flavor : "";
      this.base_ac = obj.base_ac ? new UpgradableNumber(obj.base_ac) : new UpgradableNumber();
      this.base_size = obj.base_size ? obj.base_size : 3;
      this.base_ability_scores = obj.base_ability_scores ? new AbilityScores(obj.base_ability_scores) : new AbilityScores();
      this.cr = obj.cr ? obj.cr : 0;
      this.creature_type = obj.creature_type ? obj.creature_type : "None";
      this.subtypes = obj.subtypes ? [...obj.subtypes] : [];
      this.age_category = obj.age_category ? obj.age_category : "";
      this.swarm = obj.swarm ? obj.swarm : false;
      this.tags = obj.tags ? obj.tags : [];
      this.tags = common_util.unique_array(this.tags);
      this.init = obj.init ? obj.init : 0;
      this.use_init = obj.use_init ? obj.use_init : false;

      this.hit_dice = obj.hit_dice ? obj.hit_dice.map((o: any) => new HitDiceUpgradable(o)) : [];
      this.hit_dice_bonus = obj.hit_dice_bonus ? new UpgradableNumber(obj.hit_dice_bonus) : new UpgradableNumber();
      this.max_hp = obj.max_hp ? obj.max_hp : 0;
      this.calc_max_hp_from_source = obj.calc_max_hp_from_source ? obj.calc_max_hp_from_source : false;
      this.passive_perception = obj.passive_perception ? obj.passive_perception : 0;
      this.spell_attack = obj.spell_attack ? new UpgradableNumber(obj.spell_attack) : new UpgradableNumber();
      this.spell_dc = obj.spell_dc ? new UpgradableNumber(obj.spell_dc) : new UpgradableNumber();
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "None";
      this.sc_entry = obj.sc_entry ? new SpellcastingEntry(obj.sc_entry) : undefined;

      this.summoner_attrs = obj.summoner_attrs ? obj.summoner_attrs : undefined;
      this.use_summoner_pb = obj.use_summoner_pb ? obj.use_summoner_pb : false;

      this.creature_id = obj.creature_id ? obj.creature_id : "";

      this.ff_ids = obj.ff_ids ? obj.ff_ids : {
        traits: [], actions: [], legendary_actions: [], lair_actions: []
      };
      this.inherited_spells = obj.inherited_spells ? obj.inherited_spells : [];
      this.legendary_actions_per_round = obj.legendary_actions_per_round ? obj.legendary_actions_per_round : 0;
      this.legendary_resistances = obj.legendary_resistances ? obj.legendary_resistances : 0;
      this.cgift_ids = obj.cgift_ids ? [...obj.cgift_ids] : [];
      this.creature_gifts = obj.creature_gifts ? obj.creature_gifts.map((o: any) => new CreatureInstanceGift(o)) : [];

      this.conditions = obj.conditions ? obj.conditions.map((o: any) => new CharacterCondition(o)) : [];
      this.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      this.damage_immunities = obj.damage_immunities ? obj.damage_immunities : [];
      this.damage_resistances = obj.damage_resistances ? obj.damage_resistances : [];
      this.damage_vulnerabilities = obj.damage_vulnerabilities ? obj.damage_vulnerabilities : [];
      this.extra_damage_immunities = obj.extra_damage_immunities ? obj.extra_damage_immunities : "";
      this.extra_damage_resistances = obj.extra_damage_resistances ? obj.extra_damage_resistances : "";
      this.extra_damage_vulnerabilities = obj.extra_damage_vulnerabilities ? obj.extra_damage_vulnerabilities : "";

      this.senses = obj.senses ? {...obj.senses} : {};
      this.save_profs = obj.save_profs ? [...obj.save_profs] : [];
      this.skill_profs = obj.skill_profs ? {...obj.skill_profs} : {};
      this.speeds = obj.speeds ? {...obj.speeds} : {};
      this.hover = obj.hover ? obj.hover : false;
      this.tool_profs = obj.tool_profs && obj.tool_profs.length ? obj.tool_profs : [];
      this.languages = obj.languages ? obj.languages : "";
    }
  }

  toDBObj() {
    return {
      small_name: this.small_name,
      title: this.title,
      alignment: this.alignment,
      base_ac: this.base_ac.toDBObj(),
      base_size: this.base_size,
      base_ability_scores: this.base_ability_scores.toDBObj(),
      cr: this.cr,
      ac_flavor: this.ac_flavor,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      age_category: this.age_category,
      swarm: this.swarm,
      tags: this.tags,
      init: this.init,
      use_init: this.use_init,
      hit_dice: this.hit_dice.map(o => o.toDBObj()),
      hit_dice_bonus: this.hit_dice_bonus.toDBObj(),
      max_hp: this.max_hp,
      calc_max_hp_from_source: this.calc_max_hp_from_source,
      passive_perception: this.passive_perception,
      spell_attack: this.spell_attack.toDBObj(),
      spell_dc: this.spell_dc.toDBObj(),
      spellcasting_ability: this.spellcasting_ability,
      summoner_attrs: this.summoner_attrs,
      use_summoner_pb: this.use_summoner_pb,
      creature_id: this.creature_id,
      ff_ids: this.ff_ids,
      inherited_spells: this.inherited_spells,
      legendary_actions_per_round: this.legendary_actions_per_round,
      legendary_resistances: this.legendary_resistances,
      cgift_ids: this.cgift_ids,
      creature_gifts: this.creature_gifts.map(o => o.toDBObj()),
      conditions: this.conditions.map(o => o.toDBObj()),
      condition_immunities: this.condition_immunities,
      damage_immunities: this.damage_immunities,
      damage_resistances: this.damage_resistances,
      damage_vulnerabilities: this.damage_vulnerabilities,
      extra_damage_immunities: this.extra_damage_immunities,
      extra_damage_resistances: this.extra_damage_resistances,
      extra_damage_vulnerabilities: this.extra_damage_vulnerabilities,
      senses: this.senses,
      save_profs: this.save_profs,
      skill_profs: this.skill_profs,
      speeds: this.speeds,
      hover: this.hover,
      tool_profs: this.tool_profs,
      languages: this.languages,
    };
  }

  clone(drop_ids = false): CreatureSpirit {
    const clone = new CreatureSpirit();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CreatureSpirit, drop_ids = false): void {
    this.small_name = copyMe.small_name;
    this.title = copyMe.title;
    this.alignment = copyMe.alignment;
    this.cr = copyMe.cr;
    
    this.base_ability_scores = copyMe.base_ability_scores.clone();
    this.base_size = copyMe.base_size;
    this.base_ac = copyMe.base_ac.clone();

    this.creature_type = copyMe.creature_type;
    this.subtypes = [...copyMe.subtypes];
    this.age_category = copyMe.age_category;
    this.swarm = copyMe.swarm;
    this.tags = [...copyMe.tags];

    this.ac_flavor = copyMe.ac_flavor;
    this.init = copyMe.init;
    this.use_init = copyMe.use_init;

    this.hit_dice = copyMe.hit_dice.map(o => o.clone());
    this.hit_dice_bonus = copyMe.hit_dice_bonus.clone();
    this.max_hp = copyMe.max_hp;
    this.calc_max_hp_from_source = copyMe.calc_max_hp_from_source;
    this.passive_perception = copyMe.passive_perception;
    this.spell_attack = copyMe.spell_attack.clone();
    this.spell_dc = copyMe.spell_dc.clone();
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.sc_entry = copyMe.sc_entry ? copyMe.sc_entry.clone() : undefined;

    this.summoner_attrs = copyMe.summoner_attrs ? {...copyMe.summoner_attrs} : undefined;
    this.use_summoner_pb = copyMe.use_summoner_pb;
    this.creature_id = copyMe.creature_id;
    this.creature = copyMe.creature;

    this.ff_ids = {
      traits: [...copyMe.ff_ids.traits],
      actions: [...copyMe.ff_ids.actions],
      legendary_actions: [...copyMe.ff_ids.legendary_actions],
      lair_actions: [...copyMe.ff_ids.lair_actions],
    };
    this.ffs = {
      traits: [...copyMe.ffs.traits],
      actions: [...copyMe.ffs.actions],
      legendary_actions: [...copyMe.ffs.legendary_actions],
      lair_actions: [...copyMe.ffs.lair_actions],
    };
    this.inherited_spells = [...copyMe.inherited_spells];
    this.char_spells = [...copyMe.char_spells];
    this.legendary_actions_per_round = copyMe.legendary_actions_per_round;
    this.legendary_resistances = copyMe.legendary_resistances;
    this.cgift_ids = [...copyMe.cgift_ids];
    this.creature_gifts = copyMe.creature_gifts.map(o => o.clone());
    
    this.conditions = copyMe.conditions.map(o => o.clone());
    this.condition_immunities = [...copyMe.condition_immunities];
    this.damage_immunities = [...copyMe.damage_immunities];
    this.damage_resistances = [...copyMe.damage_resistances];
    this.damage_vulnerabilities = [...copyMe.damage_vulnerabilities];
    this.extra_damage_immunities = copyMe.extra_damage_immunities;
    this.extra_damage_resistances = copyMe.extra_damage_resistances;
    this.extra_damage_vulnerabilities = copyMe.extra_damage_vulnerabilities;

    this.senses = {...copyMe.senses};
    this.save_profs = [...copyMe.save_profs];
    this.skill_profs = {...copyMe.skill_profs};
    this.speeds = {...copyMe.speeds};
    this.hover = copyMe.hover;
    this.tool_profs = [...copyMe.tool_profs];
    this.languages = copyMe.languages;
  }
}

export interface SummonerAttributes {
  ability_scores: AbilityScores,
  spellcasting_stat_score: number, // Ability Score of the Spellcasting Ability.
  pb: number, // Proficiency Bonus.
  class_level: number, // Class Level, specifically the class relevant to the summoning.
  char_level: number, // Char Level.
  slot_level: number, // Spell Slot level used
  spell_attack: number,
  spell_dc: number,
  table_entries: { table_type: string, table_id: string, entry: TableEntry }[],
}

export interface InheritedSpell {
  true_id: string;
  source_true_id: string;
}

export class CreatureInstanceGift extends CharacterFeatureSource {
  source: CreatureGift | null = null;

  get data_type(): DataType {
    return "creature_gift";
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (this.source_id === "" && obj.gift_id) {
        this.source_id = obj.gift_id;
      }
      if (this.char_feature_bases.length === 0 && obj.features && obj.features.length > 0) {
        obj.features.forEach((o: any) => {
          const fb = new CharacterFeatureBase(o);
          fb.source_id = this.source_id;
          this.char_feature_bases.push(fb);
        });
      }
    }
  }

  get damage_type_overrides_table() {
    return this.source ? this.source.damage_type_overrides_table : new DamageTypeOverridesTable();
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj
    };
  }

  clone(drop_ids = false): CreatureInstanceGift {
    const clone = new CreatureInstanceGift();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CreatureInstanceGift, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.source = copyMe.source;
  }
}